/**
 * Video renderer for the "Make video" flow. Takes a track (audio URL +
 * cover URL + title/artist), a canvas, and a set of options; drives
 * a real-time record pass that:
 *   1. Loads the cover as an ImageBitmap so drawImage is cheap.
 *   2. Creates a hidden <audio> element for the record pass — decoupled
 *      from the global player so we don't interfere with what the user
 *      might be listening to, and so we can safely disconnect / GC it
 *      when done.
 *   3. Routes audio through Web Audio: `source → { destination (speaker),
 *      streamDestination (recorder) }` — the user hears playback while
 *      it records, and the recorder gets the same signal.
 *   4. Combines `canvas.captureStream()` (video) + streamDestination
 *      (audio) into one MediaStream and hands it to MediaRecorder.
 *   5. Runs a requestAnimationFrame loop that draws each frame, calling
 *      the effect function with the current AnalyserNode data + audio time.
 *   6. Stops when the audio `ended` fires (or when the user cancels).
 *   7. Resolves with a Blob (video/webm) ready to download.
 *
 * CORS requirement: the audio URL MUST have Access-Control-Allow-Origin
 * that permits `app.mixgenie.ai`. Without it, MediaElementAudioSourceNode
 * refuses to expose audio data → recording produces silence. The audio
 * bucket (mixgenie-catalog-images) needs a CORS rule. If the constructor
 * throws on setup, that's the most likely cause.
 */

export type VideoFormat = { key: '16:9' | '9:16' | '1:1'; label: string; width: number; height: number };

export const VIDEO_FORMATS: VideoFormat[] = [
  { key: '9:16', label: 'Shorts / TikTok / Reels', width: 720, height: 1280 },
  { key: '16:9', label: 'YouTube', width: 1280, height: 720 },
  { key: '1:1', label: 'Square', width: 1080, height: 1080 },
];

export interface RenderInput {
  audioUrl: string;
  coverUrl?: string;
  title: string;
  artist?: string;
  /** Raw lyrics string. Line-broken; blank lines separate verse/chorus. */
  lyrics?: string;
  /** When true, render karaoke-style timed lines at the bottom. */
  showLyrics?: boolean;
  format: VideoFormat;
  watermark: boolean;
  canvas: HTMLCanvasElement;
  onProgress?: (fraction: number, elapsedSeconds: number) => void;
  onCancel?: () => boolean; // return true to abort
}

/**
 * Estimated line timing. Since Mureka doesn't return per-word or
 * per-line timestamps, we distribute the song's runtime across lyric
 * lines weighted by character count (longer lines get more time).
 * Blank lines get a small fixed pause. Once the audio has decoded
 * (`durationchange` fires) we resolve `startSec/endSec` for each line
 * so the draw loop can pick the active one from `audio.currentTime`.
 */
export interface LyricLine {
  text: string;
  startSec: number;
  endSec: number;
}

function planLyricTiming(lyrics: string, totalSec: number, leadInSec = 4): LyricLine[] {
  if (!lyrics || !totalSec || totalSec <= 0) return [];
  const raw = lyrics.split(/\r?\n/);
  // Weight by max(chars, 8) so short lines get a floor; blank line = 6 units.
  const weights = raw.map((l) => l.trim().length === 0 ? 6 : Math.max(8, l.trim().length));
  const totalWeight = weights.reduce((a, b) => a + b, 0);
  // Reserve a lead-in so the first line doesn't collide with the intro
  // instrumental. Also a small tail so the last line doesn't sit under
  // the outro. Both scale with totalSec so short songs still feel right.
  const clampedLeadIn = Math.min(leadInSec, totalSec * 0.15);
  const tailSec = Math.min(3, totalSec * 0.05);
  const available = Math.max(0, totalSec - clampedLeadIn - tailSec);
  let cursor = clampedLeadIn;
  const lines: LyricLine[] = [];
  for (let i = 0; i < raw.length; i++) {
    const duration = totalWeight > 0 ? (weights[i] / totalWeight) * available : 0;
    const text = raw[i].trim();
    if (text.length > 0) {
      lines.push({ text, startSec: cursor, endSec: cursor + duration });
    }
    cursor += duration;
  }
  return lines;
}

export interface RenderHandle {
  /** Resolves with the produced webm blob when the audio finishes. */
  done: Promise<Blob>;
  /** Manually stop the recording early — the promise resolves with what was captured so far. */
  cancel: () => void;
}

const FPS = 30;

export async function startVideoRender(input: RenderInput): Promise<RenderHandle> {
  const { audioUrl, coverUrl, title, artist, lyrics, showLyrics, format, watermark, canvas } = input;
  canvas.width = format.width;
  canvas.height = format.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D not available');

  const cover = coverUrl ? await loadImage(coverUrl).catch((e) => {
    // eslint-disable-next-line no-console
    console.warn('[VideoRenderer] cover failed to load — rendering placeholder', { url: coverUrl, err: e?.message });
    return null;
  }) : null;
  if (coverUrl && !cover) {
    // eslint-disable-next-line no-console
    console.warn('[VideoRenderer] cover was null after load (URL:', coverUrl, ')');
  }
  // Brand logo — same-origin (public/ asset), so no blob-fetch dance
  // needed. Loaded once here so drawScene doesn't allocate per-frame.
  const logo = watermark ? await loadSameOriginImage('/logo/logo_single.png').catch(() => null) : null;
  // Preload the brand fonts so the first frame doesn't render in
  // sans-serif fallback. `document.fonts.load` returns once each face
  // has an actual font file bound; safe to ignore failures (fallback
  // to the platform system font just makes the frame less on-brand).
  if (watermark && (document as any).fonts?.load) {
    await Promise.all([
      (document as any).fonts.load('700 48px "Poppins"'),
      (document as any).fonts.load('700 32px "Poppins"'),
      (document as any).fonts.load('500 24px "Nunito"'),
    ]).catch(() => undefined);
  }

  // Prefetch the audio into a Blob and hand the element a blob: URL.
  // The direct `crossOrigin='anonymous'` path on the CDN URL was
  // rejecting on some browsers (S3 auto-adds `Access-Control-Allow-
  // -Credentials: true` and the combo trips some strict CORS
  // implementations, producing an opaque MEDIA_ERR_SRC_NOT_SUPPORTED
  // even though a plain <audio> plays it fine). A same-origin blob URL
  // sidesteps CORS entirely for the audio element AND for
  // MediaElementAudioSourceNode's taint check.
  // `cache: 'reload'` skips the HTTP cache, and the query-string bust
  // sidesteps workbox — its cache key is the exact URL, so a unique
  // `_v=` guarantees a fresh cors fetch even when an older SW is still
  // installed on the user's device (SW updates only land on reload).
  const bustedAudio = audioUrl + (audioUrl.includes('?') ? '&' : '?') + `_v=${Date.now()}`;
  const audioBlob = await fetch(bustedAudio, { credentials: 'omit', cache: 'reload' })
    .then((r) => {
      if (!r.ok) throw new Error(`Audio fetch failed (HTTP ${r.status})`);
      return r.blob();
    });
  const blobUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio();
  audio.preload = 'auto';
  audio.src = blobUrl;

  const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const source = audioCtx.createMediaElementSource(audio);
  const analyser = audioCtx.createAnalyser();
  analyser.fftSize = 512;
  analyser.smoothingTimeConstant = 0.8;
  const streamDest = audioCtx.createMediaStreamDestination();
  source.connect(analyser);
  analyser.connect(streamDest);
  analyser.connect(audioCtx.destination); // speakers, so user hears it

  const freqData = new Uint8Array(analyser.frequencyBinCount);

  const videoStream = canvas.captureStream(FPS);
  const combined = new MediaStream([
    ...videoStream.getVideoTracks(),
    ...streamDest.stream.getAudioTracks(),
  ]);

  const mimeType = pickMimeType();
  const recorder = new MediaRecorder(combined, {
    mimeType,
    videoBitsPerSecond: 4_000_000,
    audioBitsPerSecond: 128_000,
  });

  const chunks: Blob[] = [];
  recorder.ondataavailable = (e) => {
    if (e.data && e.data.size > 0) chunks.push(e.data);
  };

  let stopped = false;
  const cancel = () => {
    if (stopped) return;
    stopped = true;
    try { audio.pause(); } catch { /* ignore */ }
    try { recorder.stop(); } catch { /* ignore */ }
  };

  const done = new Promise<Blob>((resolve, reject) => {
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: mimeType });
      // Clean up: close the audio graph AND revoke the blob URL. Both
      // leak (persistently in the case of blob: URLs) if forgotten.
      audioCtx.close().catch(() => undefined);
      URL.revokeObjectURL(blobUrl);
      resolve(blob);
    };
    recorder.onerror = (e) => { URL.revokeObjectURL(blobUrl); reject(e); };
    audio.onended = () => cancel();
    audio.onerror = () => {
      URL.revokeObjectURL(blobUrl);
      reject(new Error('Audio playback failed — try again in a moment.'));
    };
  });

  // Reset the module-level ripple state so a fresh render doesn't
  // inherit rings queued from a previous run.
  ripples.length = 0;
  lastBass = 0;

  // Kick off the animation loop and start playback + recording.
  await audio.play(); // may throw NotAllowedError if not a user gesture

  // Plan lyric timing AFTER play(): metadata (and therefore duration) is
  // usually loaded by then. If we planned before play() we'd get
  // Infinity/NaN duration on some browsers and every line would fall
  // at t=0. Recompute once duration lands if it wasn't ready.
  let lyricLines: LyricLine[] = [];
  if (showLyrics && lyrics && isFinite(audio.duration) && audio.duration > 0) {
    lyricLines = planLyricTiming(lyrics, audio.duration);
  } else if (showLyrics && lyrics) {
    audio.addEventListener('durationchange', () => {
      if (isFinite(audio.duration) && audio.duration > 0 && lyricLines.length === 0) {
        lyricLines = planLyricTiming(lyrics, audio.duration);
      }
    }, { once: true });
  }

  recorder.start(1000); // 1s data chunks — smoother progress + faster stop

  const startedAt = performance.now();
  const drawFrame = () => {
    if (stopped) return;
    if (input.onCancel && input.onCancel()) { cancel(); return; }
    analyser.getByteFrequencyData(freqData);
    drawScene(ctx, format, cover, title, artist || '', watermark ? logo : null, lyricLines, freqData, audio.currentTime);
    const totalMs = audio.duration && isFinite(audio.duration) ? audio.duration * 1000 : 0;
    if (input.onProgress) {
      const elapsed = performance.now() - startedAt;
      input.onProgress(totalMs > 0 ? Math.min(1, (audio.currentTime * 1000) / totalMs) : 0, elapsed / 1000);
    }
    requestAnimationFrame(drawFrame);
  };
  requestAnimationFrame(drawFrame);

  return { done, cancel };
}

function pickMimeType(): string {
  const candidates = [
    'video/mp4;codecs=avc1.42E01E,mp4a.40.2', // some Chromium (Safari 15+, Chrome 108+)
    'video/webm;codecs=vp9,opus',
    'video/webm;codecs=vp8,opus',
    'video/webm',
  ];
  for (const t of candidates) {
    if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported(t)) return t;
  }
  return 'video/webm';
}

// Bass-triggered ripples — module-scoped so drawScene can push into it.
// Each ripple has a birth time and fades out over ~1.2s.
const ripples: { born: number }[] = [];
let lastBass = 0;

/**
 * Scene composition, top-down:
 *   • Vertical deep gradient — a stable ground, hue drifts slowly.
 *   • Plasma layer — 3 layered radial gradients drifting through the
 *     bass palette. Provides the "sophisticated background" feel without
 *     costing per-pixel iteration.
 *   • Bass ripples — concentric rings that spawn on onset and fade out.
 *   • Circular EQ around the cover — 128 bars radiating from a ring
 *     just outside the cover, height driven by real FFT data. This is
 *     the "music wave animation" — reads as an actual visualizer, not a
 *     symbolic pulse.
 *   • Cover disc — centred, cleanly clipped, with a bass-driven glow ring.
 *   • Title / artist band under it.
 *   • Watermark chip top-right (drawn last so it always sits on top).
 */
function drawScene(
  ctx: CanvasRenderingContext2D,
  format: VideoFormat,
  cover: HTMLImageElement | ImageBitmap | null,
  title: string,
  artist: string,
  logo: HTMLImageElement | null,
  lyricLines: LyricLine[],
  freq: Uint8Array,
  t: number,
): void {
  const { width: W, height: H } = format;

  const N = freq.length;
  const avg = (from: number, to: number) => {
    let s = 0; let c = 0;
    for (let i = from; i < to; i++) { s += freq[i]; c++; }
    return c > 0 ? (s / c) / 255 : 0;
  };
  const bass = avg(0, Math.floor(N * 0.08));
  const mid = avg(Math.floor(N * 0.08), Math.floor(N * 0.5));
  const rms = avg(0, N);

  const cx = W / 2;
  const cy = H / 2;
  const shortSide = Math.min(W, H);
  const coverSize = shortSide * 0.5;

  // Bass onset detection — trip when bass jumps hard, cool down so we
  // don't spawn 30 ripples per beat. `t` is seconds, so ripples culled
  // after 1.4s.
  if (bass > 0.55 && bass - lastBass > 0.12) {
    ripples.push({ born: t });
  }
  lastBass = bass;
  while (ripples.length > 0 && t - ripples[0].born > 1.4) ripples.shift();

  ctx.save();

  // Vertical gradient ground.
  const bg = ctx.createLinearGradient(0, 0, 0, H);
  bg.addColorStop(0, '#080419');
  bg.addColorStop(0.55, '#0d0620');
  bg.addColorStop(1, '#050212');
  ctx.fillStyle = bg;
  ctx.fillRect(0, 0, W, H);

  // Plasma — three drifting radial blobs, additive-blended. Cheap and
  // hypnotic; no per-pixel work.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  const blobs = [
    { hue: (200 + t * 6) % 360, cxk: 0.5 + 0.35 * Math.sin(t * 0.31), cyk: 0.5 + 0.28 * Math.cos(t * 0.24), s: 0.55 },
    { hue: (330 + t * 4) % 360, cxk: 0.5 + 0.3 * Math.cos(t * 0.19), cyk: 0.5 + 0.32 * Math.sin(t * 0.27), s: 0.6 },
    { hue: (260 + t * 5) % 360, cxk: 0.5 + 0.22 * Math.sin(t * 0.42), cyk: 0.5 + 0.24 * Math.cos(t * 0.37), s: 0.5 },
  ];
  for (const b of blobs) {
    const bx = b.cxk * W;
    const by = b.cyk * H;
    const br = shortSide * b.s * (0.9 + rms * 0.15);
    const g = ctx.createRadialGradient(bx, by, 0, bx, by, br);
    g.addColorStop(0, `hsla(${b.hue}, 78%, ${28 + bass * 12}%, ${0.55 + rms * 0.15})`);
    g.addColorStop(1, 'hsla(0, 0%, 0%, 0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, W, H);
  }
  ctx.restore();

  // Bass ripples — concentric rings that expand outward and fade.
  ctx.save();
  ctx.globalCompositeOperation = 'lighter';
  for (const r of ripples) {
    const age = t - r.born;
    const p = age / 1.4; // 0..1
    const radius = coverSize * 0.55 + p * shortSide * 0.55;
    const alpha = (1 - p) * 0.45;
    ctx.strokeStyle = `rgba(233, 74, 143, ${alpha})`;
    ctx.lineWidth = shortSide * 0.006 * (1 - p * 0.6);
    ctx.beginPath();
    ctx.arc(cx, cy, radius, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.restore();

  // Circular EQ — 128 bars radiating from a ring just outside the cover.
  // Bar heights read from the FFT. Symmetric mirror so it looks balanced
  // whether the audio is stereo or not.
  const barCount = 128;
  const ringRadius = coverSize * 0.62;
  const barMax = shortSide * 0.18;
  ctx.save();
  ctx.translate(cx, cy);
  // Slow counter-rotation so the visualizer has motion even in quiet
  // passages — just enough to not read as static.
  ctx.rotate(t * 0.05);
  for (let i = 0; i < barCount; i++) {
    // Mirror the FFT across the vertical axis: bars 0..64 on the right,
    // 64..127 mirror it on the left.
    const half = Math.floor(barCount / 2);
    const fftIdx = i < half ? i * 2 : (barCount - i) * 2;
    // Skip the very lowest bins — they're always huge and dominate.
    const clampedIdx = Math.min(freq.length - 1, Math.max(3, fftIdx));
    const v = freq[clampedIdx] / 255;
    // Non-linear curve so quiet bars still show and loud ones don't blow out.
    const shaped = Math.pow(v, 0.7);
    const h = 3 + shaped * barMax;
    const angle = (i / barCount) * Math.PI * 2 - Math.PI / 2;
    const x1 = Math.cos(angle) * ringRadius;
    const y1 = Math.sin(angle) * ringRadius;
    const x2 = Math.cos(angle) * (ringRadius + h);
    const y2 = Math.sin(angle) * (ringRadius + h);
    // Colour: hue from teal→pink across the ring so the visualizer has
    // brand cohesion (teal is our primary brand colour).
    const hue = 165 + (i / barCount) * 200; // 165 (teal) → 365 (~pink)
    const grad = ctx.createLinearGradient(x1, y1, x2, y2);
    grad.addColorStop(0, `hsla(${hue % 360}, 85%, 55%, 0.9)`);
    grad.addColorStop(1, `hsla(${hue % 360}, 85%, 65%, 0)`);
    ctx.strokeStyle = grad;
    ctx.lineWidth = Math.max(1.5, (Math.PI * 2 * ringRadius) / barCount * 0.55);
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.stroke();
  }
  ctx.restore();

  // Cover — clean centred disc with a bass-driven outer glow ring.
  ctx.save();
  // Outer glow ring (below the cover).
  ctx.shadowColor = `rgba(43, 157, 143, ${0.45 + bass * 0.35})`; // brand teal glow
  ctx.shadowBlur = shortSide * (0.04 + bass * 0.04);
  ctx.strokeStyle = 'rgba(255,255,255,0.08)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(cx, cy, coverSize * 0.5 + 2, 0, Math.PI * 2);
  ctx.stroke();
  ctx.restore();

  // Cover itself — rounded-rect clipped, drop shadow.
  ctx.save();
  ctx.translate(cx, cy);
  ctx.shadowColor = 'rgba(0,0,0,0.6)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetY = 10;
  const cornerR = coverSize * 0.08;
  roundedRectPath(ctx, -coverSize / 2, -coverSize / 2, coverSize, coverSize, cornerR);
  ctx.clip();
  if (cover) {
    // `drawImage` with an explicit destination rect keeps aspect right
    // for square-cropped covers (which the BE writes; but be defensive
    // for legacy PNGs). Source-cover-fit: draw at cover-fill from centre.
    const iw = (cover as any).naturalWidth || (cover as any).width || coverSize;
    const ih = (cover as any).naturalHeight || (cover as any).height || coverSize;
    const scale = Math.max(coverSize / iw, coverSize / ih);
    const dw = iw * scale;
    const dh = ih * scale;
    ctx.drawImage(cover, -dw / 2, -dh / 2, dw, dh);
  } else {
    // Placeholder — brand-teal disc with the song title rendered inside
    // so it still communicates something specific about the song when
    // no cover art exists. Reads like a lyric-video card.
    const disc = ctx.createLinearGradient(-coverSize / 2, -coverSize / 2, coverSize / 2, coverSize / 2);
    disc.addColorStop(0, '#1E7E73'); // brand.600
    disc.addColorStop(1, '#0A2623'); // brand.900
    ctx.fillStyle = disc;
    ctx.fillRect(-coverSize / 2, -coverSize / 2, coverSize, coverSize);
    // Small ♪ glyph above the title so the disc still reads as "music".
    ctx.fillStyle = 'rgba(255,255,255,0.5)';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.font = `700 ${Math.round(coverSize * 0.14)}px system-ui, -apple-system, sans-serif`;
    ctx.fillText('♪', 0, -coverSize * 0.28);
    // Song title, wrapped across up to 3 lines. Uses the same brand
    // font as the wordmark so the disc feels intentional.
    ctx.font = `700 ${Math.round(coverSize * 0.10)}px "Poppins", system-ui, -apple-system, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.95)';
    drawWrappedText(ctx, title || 'MixGenie song', 0, -coverSize * 0.02, coverSize * 0.85, coverSize * 0.13, 3);
  }
  ctx.restore();

  // Title band. On 9:16 we sit it beneath the cover; on 16:9 same; on
  // 1:1 there's less room so tighter.
  const bandY = cy + coverSize / 2 + shortSide * 0.06;
  ctx.save();
  ctx.textAlign = 'center';
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.font = `700 ${Math.round(shortSide * 0.045)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
  const clippedTitle = title.length > 40 ? `${title.slice(0, 37)}…` : title;
  ctx.fillText(clippedTitle, cx, bandY);
  if (artist) {
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = `500 ${Math.round(shortSide * 0.030)}px system-ui, -apple-system, "Segoe UI", sans-serif`;
    ctx.fillText(artist, cx, bandY + shortSide * 0.05);
  }
  ctx.restore();

  // Karaoke — draws the current lyric line big and bright in the lower
  // third, with the previous and next lines smaller and dimmer above /
  // below. Timing is estimated (see planLyricTiming) so it's not
  // word-perfect, but tracks the song's structure closely enough on
  // most Mureka outputs to feel intentional.
  if (lyricLines.length > 0) {
    // Binary search for the active line. Fast enough at N=100 lines
    // but tidy vs an every-frame linear scan.
    let lo = 0; let hi = lyricLines.length - 1; let activeIdx = -1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const line = lyricLines[mid];
      if (t < line.startSec) { hi = mid - 1; }
      else if (t >= line.endSec) { lo = mid + 1; }
      else { activeIdx = mid; break; }
    }
    // Between-lines gap → fall back to the just-finished line so the
    // words don't disappear on every micro-pause.
    if (activeIdx < 0) {
      for (let i = lyricLines.length - 1; i >= 0; i--) {
        if (t >= lyricLines[i].startSec) { activeIdx = i; break; }
      }
    }

    if (activeIdx >= 0) {
      const active = lyricLines[activeIdx];
      const prev = activeIdx > 0 ? lyricLines[activeIdx - 1] : null;
      const next = activeIdx < lyricLines.length - 1 ? lyricLines[activeIdx + 1] : null;
      // Progress through the active line (0..1) — feeds a subtle
      // scale + brightness pulse so the line reads as advancing.
      const lineDur = Math.max(0.001, active.endSec - active.startSec);
      const lineP = Math.min(1, Math.max(0, (t - active.startSec) / lineDur));

      const bandTop = cy + coverSize / 2 + shortSide * 0.14;
      const activeY = bandTop + shortSide * 0.07;
      const contextY = shortSide * 0.055; // vertical offset for prev/next
      const activeSize = Math.round(shortSide * 0.052);
      const contextSize = Math.round(shortSide * 0.032);
      const maxWidth = W - shortSide * 0.1;

      ctx.save();
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Prev line (fading out).
      if (prev) {
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.font = `500 ${contextSize}px "Nunito", system-ui, -apple-system, sans-serif`;
        drawWrappedText(ctx, prev.text, cx, activeY - contextY, maxWidth, contextSize * 1.15, 1);
      }
      // Next line (previewing).
      if (next) {
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.font = `500 ${contextSize}px "Nunito", system-ui, -apple-system, sans-serif`;
        drawWrappedText(ctx, next.text, cx, activeY + contextY, maxWidth, contextSize * 1.15, 1);
      }

      // Active line — brand-teal glow that intensifies over the line's
      // life, so the last quarter feels like it's leading into the next.
      const glow = 0.6 + lineP * 0.4;
      ctx.shadowColor = `rgba(98, 183, 170, ${glow})`; // brand.400 glow
      ctx.shadowBlur = shortSide * 0.02;
      ctx.fillStyle = 'rgba(255,255,255,0.98)';
      ctx.font = `800 ${activeSize}px "Poppins", system-ui, -apple-system, sans-serif`;
      drawWrappedText(ctx, active.text, cx, activeY, maxWidth, activeSize * 1.15, 2);

      ctx.restore();
    }
  }

  // Watermark chip — top-right. On-brand: real MixGenie logo + Poppins
  // wordmark in brand teal on a soft dark pill, plus a smaller Nunito
  // tagline underneath. Sized big enough to READ on a phone at social-
  // media compression: ~4% of the short side for the logo, matching
  // wordmark height, ~3% tagline. The pill background carries the mark
  // over any cover art / effect without hurting legibility.
  if (logo) {
    const pad = shortSide * 0.028;
    const logoSize = shortSide * 0.075;
    const wordSize = shortSide * 0.055; // "MixGenie"
    const tagSize = shortSide * 0.022;  // "created with mixgenie.ai"
    const gap = shortSide * 0.015;      // between logo and text

    // Measure the wordmark + tag to size the chip. Set the fonts BEFORE
    // measuring — measureText uses whatever ctx.font is at call-time.
    ctx.save();
    ctx.font = `700 ${Math.round(wordSize)}px "Poppins", system-ui, -apple-system, "Segoe UI", sans-serif`;
    const wordWidth = ctx.measureText('MixGenie').width;
    ctx.font = `500 ${Math.round(tagSize)}px "Nunito", system-ui, -apple-system, "Segoe UI", sans-serif`;
    const tagWidth = ctx.measureText('created with mixgenie.ai').width;
    ctx.restore();

    const textBlockW = Math.max(wordWidth, tagWidth);
    const chipPadX = shortSide * 0.022;
    const chipPadY = shortSide * 0.014;
    const chipW = logoSize + gap + textBlockW + chipPadX * 2;
    const chipH = Math.max(logoSize, wordSize + tagSize * 1.1) + chipPadY * 2;
    const chipX = W - pad - chipW;
    const chipY = pad;

    ctx.save();
    // Soft dark pill — semi-transparent so the effect underneath still
    // breathes through, with a subtle brand-teal border pulse.
    roundedRectPath(ctx, chipX, chipY, chipW, chipH, chipH * 0.28);
    ctx.fillStyle = 'rgba(11, 24, 48, 0.62)';
    ctx.fill();
    ctx.lineWidth = Math.max(1, shortSide * 0.0015);
    ctx.strokeStyle = 'rgba(43, 157, 143, 0.55)'; // brand.500 @ 55%
    ctx.stroke();
    ctx.restore();

    // Logo on the left of the chip, vertically centred.
    const logoX = chipX + chipPadX;
    const logoY = chipY + (chipH - logoSize) / 2;
    ctx.drawImage(logo, logoX, logoY, logoSize, logoSize);

    // Text stack to the right of the logo.
    const textX = logoX + logoSize + gap;
    // Poppins wordmark — brand teal (a touch brighter than #2B9D8F so
    // it holds up against the dark pill and doesn't disappear when the
    // video is re-compressed by TikTok/IG).
    ctx.save();
    ctx.font = `700 ${Math.round(wordSize)}px "Poppins", system-ui, -apple-system, "Segoe UI", sans-serif`;
    ctx.textBaseline = 'top';
    ctx.fillStyle = '#62B7AA'; // brand.400 — reads cleaner on dark
    ctx.fillText('MixGenie', textX, chipY + chipPadY);
    // Nunito tagline — muted white so it doesn't compete with the mark.
    ctx.font = `500 ${Math.round(tagSize)}px "Nunito", system-ui, -apple-system, "Segoe UI", sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.78)';
    ctx.fillText('created with mixgenie.ai', textX, chipY + chipPadY + wordSize * 1.05);
    ctx.restore();
  }

  ctx.restore();
}

/**
 * Word-wrap `text` into up to `maxLines` lines fitting within `maxWidth`.
 * Centres the block vertically around `(cx, cy)`. Uses the current
 * ctx.font and ctx.textAlign — caller sets those before calling.
 * Overflow is truncated with an ellipsis on the last line so long
 * titles don't tumble off the disc.
 */
function drawWrappedText(
  ctx: CanvasRenderingContext2D,
  text: string,
  cx: number,
  cy: number,
  maxWidth: number,
  lineHeight: number,
  maxLines: number,
): void {
  const words = String(text || '').trim().split(/\s+/);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const attempt = current ? `${current} ${word}` : word;
    if (ctx.measureText(attempt).width <= maxWidth) {
      current = attempt;
    } else {
      if (current) lines.push(current);
      current = word;
      if (lines.length === maxLines) break;
    }
  }
  if (current && lines.length < maxLines) lines.push(current);
  // If the text overflowed, add an ellipsis to the last line.
  if (lines.length === maxLines) {
    let last = lines[maxLines - 1];
    const restWords = words.slice(lines.join(' ').split(/\s+/).length);
    if (restWords.length > 0) {
      while (last && ctx.measureText(last + '…').width > maxWidth) {
        last = last.slice(0, -1);
      }
      lines[maxLines - 1] = last + '…';
    }
  }
  const totalH = lines.length * lineHeight;
  const startY = cy - totalH / 2 + lineHeight / 2;
  for (let i = 0; i < lines.length; i++) {
    ctx.fillText(lines[i], cx, startY + i * lineHeight);
  }
}

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number): void {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

/**
 * Simple same-origin image loader. Use for assets that live under the
 * app's own domain (e.g. bundled `/logo/*.png`) — no CORS gymnastics
 * needed and no canvas taint.
 */
function loadSameOriginImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`Failed to load ${url}`));
    img.src = url;
  });
}

async function loadImage(url: string): Promise<HTMLImageElement> {
  // Fetch → blob → object URL so the canvas isn't tainted by a cross-
  // origin draw. Even with CORS set on the bucket, `img.crossOrigin`
  // taints unpredictably on some browsers; a same-origin blob: URL
  // sidesteps the whole problem. Falls back to a direct <img> load if
  // fetch fails so the cover still shows up when it can (canvas may
  // still taint — but captureStream will just refuse and the outer
  // catch will surface a clean error).
  try {
    // See the audio fetch above — same SW / opaque-response trap
    // applies to cover images that were also loaded no-cors by list
    // rows / <img> tags earlier in the session. Cache-bust the URL too:
    // workbox keys its cache by exact URL, so a unique query param
    // guarantees a fresh cors-mode fetch even if an older SW is still
    // installed on the user's device (SW updates land on next reload).
    const busted = url + (url.includes('?') ? '&' : '?') + `_v=${Date.now()}`;
    const blob = await fetch(busted, { credentials: 'omit', cache: 'reload' }).then((r) => {
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      return r.blob();
    });
    const blobUrl = URL.createObjectURL(blob);
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Cover blob decode failed'));
      img.src = blobUrl;
      // Don't revoke here — the img needs the URL alive for the whole
      // render pass. It'll be GC'd with the img reference at end of run.
    });
  } catch {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Cover failed to load'));
      img.src = url;
    });
  }
}
